// <WorldStage> — the standard frame every theme-world renderer sits in.
//
// WHY THIS EXISTS. The renderers used to put `overflow-y-auto` on their own
// root and drop <ThemeWorldAtmosphere> + <SoundToggle> inside it. Both are
// `position: absolute`, so their containing block was the SCROLL container:
// they were sized to one viewport, glued to the top of the content, and slid
// away as soon as the learner scrolled — leaving bare background below the
// fold and no way to reach the sound toggle. A `sticky` header inside that
// same root had the matching problem: its offset was measured from the scroll
// container's own top edge, so a non-zero `top` pushed it DOWN over the content
// underneath it even at scrollTop 0.
//
// The fix is one level of nesting. The stage does not scroll:
//
//   .tw-stage            position: relative; overflow: hidden   ← atmosphere + toggle anchor here
//     └ atmosphere       position: absolute; inset: 0           ← covers the whole slide, always
//     └ sound toggle     position: absolute; top/right          ← stays reachable
//     └ .tw-stage-scroll overflow-y: auto                       ← the only scrolling box
//         └ children                                            ← `sticky top-0` works correctly here
//
// The stage owns the world's background/text/font so children can stay
// transparent and let the atmosphere through.

import type { ReactNode, CSSProperties } from 'react';
import type { Theme, ThemeId } from '../../components/course-builder/slides/slideTypes';
import { ThemeWorldAtmosphere } from './Atmosphere';
import { SoundToggle } from './SoundToggle';

export function WorldStage({
  worldId,
  theme,
  audioEnabled,
  onToggleAudio,
  className,
  scrollClassName,
  motesCount,
  children,
}: {
  worldId: ThemeId;
  theme: Theme;
  audioEnabled: boolean;
  onToggleAudio: () => void;
  /** extra classes on the non-scrolling stage (e.g. a renderer's CSS namespace) */
  className?: string;
  /** extra classes on the inner scroller */
  scrollClassName?: string;
  motesCount?: number;
  children: ReactNode;
}) {
  const stageStyle: CSSProperties = {
    position: 'relative',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    background: theme.colors.background,
    color: theme.colors.text,
    fontFamily: theme.fonts.body,
  };

  return (
    <div className={className} style={stageStyle}>
      <ThemeWorldAtmosphere
        worldId={worldId}
        primary={theme.colors.primary}
        accent={theme.colors.accent || theme.colors.primary}
        background={theme.colors.background}
        motesCount={motesCount}
        style={{ zIndex: 0 }}
      />

      <SoundToggle enabled={audioEnabled} onToggle={onToggleAudio} theme={theme} />

      <div
        className={scrollClassName}
        style={{ position: 'relative', zIndex: 10, height: '100%', overflowY: 'auto' }}
      >
        {children}
      </div>
    </div>
  );
}
